import { create } from 'zustand';
import { NIGERIAN_BENCHMARKS, NIGERIAN_SOLAR_PRESETS, SystemPreset, COMMON_APPLIANCES } from '../lib/constants';
import { calculateSolarMetrics, SolarMetricsResult } from '../lib/solarPhysics';

export interface SelectedAppliance {
  id: string;
  count: number;
  customWatts?: number;
  customHours?: number;
}

interface SolarStore {
  selectedPresetId: string;
  selectedState: string;
  peakSunHours: number;
  petrolPrice: number;
  dieselPrice: number;
  gridTariff: number;
  generatorUsageHours: number;
  selectedAppliances: SelectedAppliance[];
  timeOfDay: number;
  currentSoc: number; // batterySoc
  sunlightIntensity: number; // 0-100%
  gridAvailable: boolean; // PHCN status
  activeLoadWatts: number;
  selectedPreset: SystemPreset;
  presets: SystemPreset[];
  calculatedMetrics: SolarMetricsResult;
  activeTab: 'calculator' | 'packages' | 'dashboard' | 'consultation' | 'financing' | 'audit' | 'admin' | 'growth' | 'ai-tools';
  isLeadModalOpen: boolean;
  leadConsultationType: string;
  
  // Actions
  fetchPresets: () => Promise<void>;
  addPreset: (preset: SystemPreset) => Promise<void>;
  updatePreset: (preset: SystemPreset) => Promise<void>;
  deletePreset: (id: string) => Promise<void>;
  setPresets: (presets: SystemPreset[]) => void;
  setSelectedPreset: (presetId: string) => void;
  selectPreset: (preset: SystemPreset) => void;
  setSelectedState: (stateName: string) => void;
  setPetrolPrice: (val: number) => void;
  setDieselPrice: (val: number) => void;
  setGridTariff: (val: number) => void;
  setGeneratorUsageHours: (val: number) => void;
  updateApplianceCount: (applianceId: string, count: number) => void;
  setTimeOfDay: (hour: number) => void;
  setCurrentSoc: (soc: number) => void;
  updateSoc: (soc: number) => void;
  setSunlight: (intensity: number) => void;
  toggleGrid: () => void;
  setActiveLoad: (watts: number) => void;
  setActiveTab: (tab: 'calculator' | 'packages' | 'dashboard' | 'consultation' | 'financing' | 'audit' | 'admin' | 'growth' | 'ai-tools') => void;
  setIsLeadModalOpen: (isOpen: boolean, consultationType?: string) => void;
  recalculate: () => void;
  
  // Computed helpers
  getMetrics: () => SolarMetricsResult;
  getTotalLoadWatts: () => number;
  getActivePreset: () => SystemPreset;
}

export const useSolarStore = create<SolarStore>((set, get) => ({
  selectedPresetId: 'standard_3_5kva',
  selectedState: 'Lagos State',
  peakSunHours: 4.8,
  petrolPrice: NIGERIAN_BENCHMARKS.petrolPricePerLiter,
  dieselPrice: NIGERIAN_BENCHMARKS.dieselPricePerLiter,
  gridTariff: NIGERIAN_BENCHMARKS.bandATariffPerKwh,
  generatorUsageHours: 6,
  selectedAppliances: [
    { id: 'ac_1hp', count: 1 },
    { id: 'fridge', count: 1 },
    { id: 'tv_55', count: 1 },
    { id: 'lighting', count: 1 },
    { id: 'workstation', count: 1 },
  ],
  timeOfDay: 13,
  currentSoc: 88,
  sunlightIntensity: 90,
  gridAvailable: true,
  activeLoadWatts: 1800,
  selectedPreset: NIGERIAN_SOLAR_PRESETS[1],
  presets: NIGERIAN_SOLAR_PRESETS,
  calculatedMetrics: calculateSolarMetrics({
    solarPanelCapacityKw: NIGERIAN_SOLAR_PRESETS[1].solarPanelCapacityKw,
    batteryCapacityKwh: NIGERIAN_SOLAR_PRESETS[1].batteryCapacityKwh,
    inverterCapacityKw: NIGERIAN_SOLAR_PRESETS[1].inverterCapacityKw,
    totalLoadWatts: 1800,
    peakSunHours: 4.8,
    petrolPricePerLiter: NIGERIAN_BENCHMARKS.petrolPricePerLiter,
    dieselPricePerLiter: NIGERIAN_BENCHMARKS.dieselPricePerLiter,
    gridTariffPerKwh: NIGERIAN_BENCHMARKS.bandATariffPerKwh,
    generatorUsageHoursPerDay: 6,
    timeOfDay: 13,
  }, 88),
  activeTab: 'calculator',
  isLeadModalOpen: false,
  leadConsultationType: 'Standard Energy Audit & Quote',

  fetchPresets: async () => {
    try {
      const res = await fetch('/api/presets');
      const data = await res.json();
      if (data.success && data.presets) {
        set({ presets: data.presets });
        const currentPreset = data.presets.find(p => p.id === get().selectedPresetId) || data.presets[0];
        if (currentPreset) {
          set({ selectedPreset: currentPreset });
        }
        get().recalculate();
      }
    } catch (err) {
      console.error("Error fetching presets:", err);
    }
  },

  addPreset: async (preset) => {
    try {
      const res = await fetch('/api/admin/presets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(preset),
      });
      const data = await res.json();
      if (data.success) {
        await get().fetchPresets();
      }
    } catch (err) {
      console.error("Error adding preset:", err);
    }
  },

  updatePreset: async (preset) => {
    try {
      const res = await fetch('/api/admin/presets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(preset),
      });
      const data = await res.json();
      if (data.success) {
        await get().fetchPresets();
      }
    } catch (err) {
      console.error("Error updating preset:", err);
    }
  },

  deletePreset: async (id) => {
    try {
      const res = await fetch(`/api/admin/presets/${id}`, {
        method: 'DELETE',
      });
      const data = await res.json();
      if (data.success) {
        await get().fetchPresets();
      }
    } catch (err) {
      console.error("Error deleting preset:", err);
    }
  },

  setPresets: (presets) => {
    set({ presets });
    const currentPreset = presets.find(p => p.id === get().selectedPresetId) || presets[0];
    if (currentPreset) {
      set({ selectedPreset: currentPreset });
    }
    get().recalculate();
  },

  setSelectedPreset: (presetId) => {
    const preset = get().presets.find(p => p.id === presetId) || get().presets[0];
    set({ selectedPresetId: presetId, selectedPreset: preset });
    get().recalculate();
  },

  selectPreset: (preset) => {
    set({ selectedPresetId: preset.id, selectedPreset: preset });
    get().recalculate();
  },

  setSelectedState: (stateName) => {
    set({ selectedState: stateName });
    if (stateName.includes('Abuja')) set({ peakSunHours: 5.5 });
    else if (stateName.includes('Kano')) set({ peakSunHours: 6.0 });
    else if (stateName.includes('Port Harcourt')) set({ peakSunHours: 4.6 });
    else if (stateName.includes('Ibadan')) set({ peakSunHours: 5.1 });
    else set({ peakSunHours: 4.8 });
    get().recalculate();
  },

  setPetrolPrice: (val) => { set({ petrolPrice: val }); get().recalculate(); },
  setDieselPrice: (val) => { set({ dieselPrice: val }); get().recalculate(); },
  setGridTariff: (val) => { set({ gridTariff: val }); get().recalculate(); },
  setGeneratorUsageHours: (val) => { set({ generatorUsageHours: val }); get().recalculate(); },

  updateApplianceCount: (applianceId, count) => {
    const current = get().selectedAppliances;
    const existingIndex = current.findIndex(a => a.id === applianceId);
    if (existingIndex >= 0) {
      if (count <= 0) {
        set({ selectedAppliances: current.filter(a => a.id !== applianceId) });
      } else {
        const updated = [...current];
        updated[existingIndex] = { ...updated[existingIndex], count };
        set({ selectedAppliances: updated });
      }
    } else if (count > 0) {
      set({ selectedAppliances: [...current, { id: applianceId, count }] });
    }
    const totalWatts = get().getTotalLoadWatts();
    set({ activeLoadWatts: totalWatts });
    get().recalculate();
  },

  setTimeOfDay: (hour) => { set({ timeOfDay: hour }); get().recalculate(); },
  setCurrentSoc: (soc) => { set({ currentSoc: soc }); get().recalculate(); },
  updateSoc: (soc) => { set({ currentSoc: soc }); get().recalculate(); },
  
  setSunlight: (intensity) => { set({ sunlightIntensity: intensity }); get().recalculate(); },
  toggleGrid: () => { set((state) => ({ gridAvailable: !state.gridAvailable })); get().recalculate(); },
  
  setActiveLoad: (watts) => { set({ activeLoadWatts: watts }); get().recalculate(); },

  setActiveTab: (tab) => set({ activeTab: tab }),
  setIsLeadModalOpen: (isOpen, consultationType = 'Standard Energy Audit & Quote') => 
    set({ isLeadModalOpen: isOpen, leadConsultationType: consultationType }),

  getTotalLoadWatts: () => {
    const { selectedAppliances, activeLoadWatts } = get();
    const fromAppliances = selectedAppliances.reduce((total, item) => {
      const def = COMMON_APPLIANCES.find(a => a.id === item.id);
      const watts = def ? def.defaultWatts : 100;
      return total + watts * item.count;
    }, 0);
    return Math.max(fromAppliances, activeLoadWatts);
  },

  getActivePreset: () => {
    const { selectedPresetId, presets } = get();
    return presets.find(p => p.id === selectedPresetId) || presets[0];
  },

  getMetrics: () => {
    return get().calculatedMetrics;
  },

  recalculate: () => {
    const state = get();
    const preset = state.getActivePreset();
    const totalLoadWatts = state.getTotalLoadWatts();

    const metrics = calculateSolarMetrics({
      solarPanelCapacityKw: preset.solarPanelCapacityKw * (state.sunlightIntensity / 100),
      batteryCapacityKwh: preset.batteryCapacityKwh,
      inverterCapacityKw: preset.inverterCapacityKw,
      totalLoadWatts,
      peakSunHours: state.peakSunHours,
      petrolPricePerLiter: state.petrolPrice,
      dieselPricePerLiter: state.dieselPrice,
      gridTariffPerKwh: state.gridTariff,
      generatorUsageHoursPerDay: state.generatorUsageHours,
      timeOfDay: state.timeOfDay,
    }, state.currentSoc);

    set({ calculatedMetrics: metrics, activeLoadWatts: totalLoadWatts });
  }
}));

